import React, { Fragment } from 'react'
import styled from 'styled-components'
import { em } from '../../../../../utils/styles'

import { PROJECTIONS_VALUES } from '../../constants'

import { FormattedMessage } from '@dragonraider5/react-intl'
import { Link } from 'react-router-dom'
import { showFieldError, renderValidation } from '../../../../../utils/validations'
import { getExceptionMessage } from '../../../../../utils/exceptions'
import Button from '../../../../../components/Button'
import FormRow from '../../../../../components/FormFields/FormRow'
import TextField from '../../../../../components/FormFields/TextField'
import SelectField from '../../../../../components/FormFields/SelectField'
import FormActions from '../../../../../components/FormFields/FormAction/FormActions'
import FormError from '../../../../../components/FormFields/FormError'
import valueMessages from './valueMessages'

import { getApplyToStepPath } from '../../../../MainRoutes/utils'
import { stepStatuses } from '../../../constants'
import { StatusSteps } from '../../StatusSteps'
import Label from '../../../../../components/FormFields/Label'
import CheckboxField from '../../../../../components/FormFields/CheckboxField'
import Fieldset from '../../../../../components/FormFields/Fieldset'
import InfoBox from '../../../../../components/InfoBox'
import { ReactComponent as PlusIcon } from '../../../../../components/Icons/icon-plus.svg'
import { ReactComponent as AddIcon } from '../../../../../components/Icons/add-document-icon.svg'
import { SectionSubTitle } from '../../../../../components/Section'
import { FieldArray } from 'formik'
import { AddButton } from '../../../../../components/FormFields/FieldList'
import FinancialFormFields from './FinancialFormFields'

const FinancingWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin-top: 1em;
  margin-bottom: -1em;
  padding-left: 1.5em;
`

const RemoveIcon = styled(PlusIcon)`
  transform: rotate(45deg);
  margin-left: ${em(5)};
  margin-bottom: ${em(-3)};
`

const makeOnNumberValueChange = (name, { setFieldValue }) =>
  values => setFieldValue(name, values.floatValue)

const initialBankingPoolItem = {
  financing_type: '',
  amount: '',
  interest_rate: '',
  entity: '',
  expiration: '',
  share: null
}

function FinancialForm({
  values,
  status = {},
  errors,
  touched,
  isSubmitting,
  setFieldValue,
  handleChange,
  handleBlur,
  handleSubmit,
  application
}) {

  const handleHasFinancing = (bool) => {
    if (bool && values.banking_pool.length === 0) {
      setFieldValue('banking_pool', [initialBankingPoolItem])
    }
    if (!bool) {
      setFieldValue('banking_pool', [])
    }
    setFieldValue('has_financing', bool)
  }

  if (values.has_financing && values.banking_pool.length === 0) {
    setFieldValue('banking_pool', [initialBankingPoolItem])
  }
  

  return (
    <form onSubmit={handleSubmit}>
      <FormRow>
        <TextField
          type='numeric'
          name='capital_invested'
          value={values.capital_invested}
          onValueChange={makeOnNumberValueChange('capital_invested', { setFieldValue })}
          onBlur={handleBlur}
          error={showFieldError('capital_invested', { errors, touched })}
          help={renderValidation('capital_invested', { errors, touched })}
          label='¿Cuánto capital llevas invertido en la organización?'
          suffix='€'
          thousandSeparator='.'
          decimalSeparator=','
          allowNegative={false}
          full
        />
      </FormRow>
      <FormRow>
        <FinancingWrapper>
          <Label>¿Llevas utilizada alguna financiación hasta la fecha?</Label>
          <CheckboxField
            type='checkbox'
            name='has_financing'
            checked={values.has_financing === false}
            onChange={() => handleHasFinancing(false)}
            error={showFieldError('has_financing', { errors, touched })}
            label={'No'}
          />
          <CheckboxField
            type='checkbox'
            name='has_financing'
            checked={values.has_financing === true}
            onChange={() => handleHasFinancing(true)}
            error={showFieldError('has_financing', { errors, touched })}
            label={'Sí'}
          />
        </FinancingWrapper>
        {(values.has_financing || values.banking_pool.length > 0) && (
            <FieldArray
              name='banking_pool'
              render={helpers => (
                <Fieldset as={InfoBox}>
                  {values.banking_pool.map((value, index) => {
                    return (
                    <Fragment key={index}>
                      <SectionSubTitle as='legend' style={{marginTop: index === 0 ? '0' : '1.75em'}}>
                        Financiación vigente {index+1}:
                      </SectionSubTitle>
                      <FinancialFormFields
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        namePrefix={`banking_pool[${index}].`}
                      />
                      <AddButton
                        title='Eliminar'
                        onClick={() => helpers.remove(index)}
                      >
                        Eliminar financiación
                        <RemoveIcon />
                      </AddButton>
                    </Fragment>
                  )})}
                  <AddButton
                     onClick={() => helpers.push(initialBankingPoolItem)}
                  >
                    Añadir otra financiación vigente<AddIcon />
                  </AddButton>
                </Fieldset>
            )}
          />
        )}
      </FormRow>
      <FormRow>
        <TextField
          type='numeric'
          name='annual_average_income'
          value={values.annual_average_income}
          onValueChange={makeOnNumberValueChange('annual_average_income', { setFieldValue })}
          onBlur={handleBlur}
          error={showFieldError('annual_average_income', { errors, touched })}
          help={renderValidation('annual_average_income', { errors, touched })}
          suffix='€'
          thousandSeparator='.'
          decimalSeparator=','
          allowNegative={false}
          label='¿Qué ingresos medios anuales tienes?'
          full
        />
      </FormRow>
      <FormRow>
        <TextField
          type='text'
          name='important_expenses'
          value={values.important_expenses}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showFieldError('important_expenses', { errors, touched })}
          help={renderValidation('important_expenses', { errors, touched })}
          label='¿Cuáles son tus gastos más importantes?'
          full
        />
      </FormRow>
      <FormRow>
        <SelectField
          name='prospects_projection'
          value={values.prospects_projection}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showFieldError('prospects_projection', { errors, touched })}
          help={renderValidation('prospects_projection', { errors, touched })}
          label='¿Qué perspectivas tienes para el año que viene?'
          full
        >
          <option value='' />
          {Object.values(PROJECTIONS_VALUES).map(value => (
            <FormattedMessage key={value} {...valueMessages[`projectionTypes${value}`]}>
              {message => <option value={value}>{message}</option>}
            </FormattedMessage>
          ))}
        </SelectField>
      </FormRow>
      <FormRow>
        <TextField
          type='text'
          name='real_state_linked'
          value={values.real_state_linked}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showFieldError('real_state_linked', { errors, touched })}
          help={renderValidation('real_state_linked', { errors, touched })}
          label='¿Qué bienes inmuebles tienes vinculados a la actividad?'
          full
        />
      </FormRow>
      <FormRow>
        <TextField
          type='textarea'
          name='text'
          value={values.text}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showFieldError('text', { errors, touched })}
          help={renderValidation('text', { errors, touched })}
          label='¿Quieres contarnos algo más sobre tu misión o actividad?'
          tip='Si lo deseas cuéntanos todo aquello que consideres relevante en el desempeño de tu misión o actividad. Triodos Bank tendrá en cuenta toda ella en el estudio para seguir desempeñando su labor social, medioambiental y/o cultural'
          forceTip
          full
        />
      </FormRow>
      {status.error && (
        <FormError>
          <FormattedMessage {...getExceptionMessage(status.error)} />
        </FormError>
      )}
      <FormActions>
        <Button type='submit' disabled={isSubmitting}>Continuar</Button>
        <Link to={getApplyToStepPath(stepStatuses.ACTIVITY, application)}>Volver</Link>
      </FormActions>
      <StatusSteps />
    </form>
  )
}

export default FinancialForm
